import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CrmPaginationComponent } from '../crm-pagination/crm-pagination.component';
import { AddDealModalComponent } from './add-deal-modal/add-deal-modal.component';
import { ViewDealModalComponent } from './view-deal-modal/view-deal-modal.component';
import * as CrmActions from '../../../state/crm/crm.actions';
import * as CrmSelectors from '../../../state/crm/crm.selectors';
import { ReportsSortComponent, SortOption, SortChoice, DEFAULT_SORT } from '../../reports/reports-sort/reports-sort.component';
import { ReportsDateRangeComponent, DateRange, DEFAULT_DATE_RANGE } from '../../reports/reports-date-range/reports-date-range.component';

const DEAL_SORT_CHOICES: SortChoice[] = [
  { label: 'Date created (newest first)', sort_by: 'created_at', sort_dir: 'desc' },
  { label: 'Date created (oldest first)', sort_by: 'created_at', sort_dir: 'asc'  },
  { label: 'Name A → Z',                  sort_by: 'deal_name',  sort_dir: 'asc'  },
  { label: 'Name Z → A',                  sort_by: 'deal_name',  sort_dir: 'desc' },
];

@Component({
  selector: 'app-deals',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, CrmPaginationComponent, AddDealModalComponent, ViewDealModalComponent, ReportsSortComponent, ReportsDateRangeComponent],
  templateUrl: './deals.component.html',
  styleUrl: './deals.component.scss'
})
export class DealsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() searchQuery: string = '';
  @Output() searchQueryChange = new EventEmitter<string>();

  showAddDealModal: boolean = false;
  showViewDealModal: boolean = false;
  selectedDeal: any = null;
  modalMode: 'create' | 'edit' = 'create';

  // Observables from store
  deals$: Observable<any[]>;
  loading$: Observable<boolean>;
  error$: Observable<any>;
  pagination$: Observable<any>;
  contacts$: Observable<any[]>;
  companies$: Observable<any[]>;
  contactsLookup$: Observable<any[]>;
  companiesLookup$: Observable<any[]>;

  // Local state
  paginatedDeals: any[] = [];
  tableHeight: number = 0;
  pagination: any = {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 10
  };

  currentSort: SortOption = { ...DEFAULT_SORT };
  currentDateRange: DateRange = DEFAULT_DATE_RANGE;
  readonly sortChoices: SortChoice[] = DEAL_SORT_CHOICES;

  private destroy$ = new Subject<void>();

  constructor(private store: Store) {
    // Set up observables with selector names
    this.deals$ = this.store.select(CrmSelectors.selectDeals);
    this.loading$ = this.store.select(CrmSelectors.selectDealsLoading);
    this.error$ = this.store.select(CrmSelectors.selectDealsError);
    this.pagination$ = this.store.select(CrmSelectors.selectDealsPagination);
    this.contacts$ = this.store.select(CrmSelectors.selectContacts);
    this.companies$ = this.store.select(CrmSelectors.selectCompanies);
    this.contactsLookup$ = this.store.select(CrmSelectors.selectContactsLookup);
    this.companiesLookup$ = this.store.select(CrmSelectors.selectCompaniesLookup);
  }

  ngOnInit(): void {
    this.updateTableHeight();

    // Don't load data here - parent CRM component handles initial loading
    // Individual loading only happens on pagination/search

    // Subscribe to pagination changes
    this.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pagination => {
        this.pagination = pagination || {
          total: 0,
          per_page: 10,
          current_page: 1,
          last_page: 1,
          from: 0,
          to: 0
        };
      });

    // Subscribe to deals changes
    this.deals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(deals => {
        // Ensure deals is always an array
        this.paginatedDeals = Array.isArray(deals) ? deals : [];
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Search is now triggered manually via button click or Enter key
    // Removed automatic search on searchQuery change to prevent search while typing
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateTableHeight();
  }

  updateTableHeight(): void {
    const headerHeight = 80; // CRM header
    const tabsHeight = 36; // Tabs
    const toolbarHeight = 42; // Period + Sort row (button height + mb-3 spacing)
    const paginationHeight = 36; // Pagination component height
    const padding = 112; // Various paddings

    this.tableHeight = window.innerHeight - headerHeight - tabsHeight - toolbarHeight - paginationHeight - padding;
  }

  onPageChanged(page: number): void {
    this.store.dispatch(CrmActions.loadDeals(this.buildLoadPayload({ page })));
  }

  onPerPageChanged(perPage: number): void {
    this.store.dispatch(CrmActions.loadDeals(this.buildLoadPayload({ page: 1, perPage })));
  }

  searchReport(): void {
    this.store.dispatch(CrmActions.loadDeals(this.buildLoadPayload({ page: 1 })));
  }

  onSortChanged(sort: SortOption): void {
    this.currentSort = sort;
    this.store.dispatch(CrmActions.loadDeals(this.buildLoadPayload({ page: 1 })));
  }

  onDateRangeChanged(range: DateRange): void {
    this.currentDateRange = range;
    this.store.dispatch(CrmActions.loadDeals(this.buildLoadPayload({ page: 1 })));
  }

  private buildLoadPayload(overrides: { page?: number; perPage?: number } = {}) {
    return {
      page: overrides.page ?? this.pagination.current_page ?? 1,
      perPage: overrides.perPage ?? this.pagination.per_page ?? 10,
      search: this.searchQuery,
      sortBy: this.currentSort.sort_by,
      sortDir: this.currentSort.sort_dir,
      dateRange: this.currentDateRange,
    };
  }

  viewDeal(deal: any): void {
    this.store.dispatch(CrmActions.loadDealDetail({ id: deal.id.toString() }));
    this.showViewDealModal = true;
  }

  editDeal(deal: any): void {
    // console.log('Edit deal:', deal);
    this.selectedDeal = deal;
    this.modalMode = 'edit';
    this.showAddDealModal = true;
  }

  deleteDeal(deal: any): void {
    if (confirm(`Are you sure you want to delete the deal "${deal.name}"?`)) {
      this.store.dispatch(CrmActions.deleteDeal({ id: deal.id }));
    }
  }

  addDeal(): void {
    // console.log('Opening deal modal with contacts:', this.contacts$);
    // console.log('Opening deal modal with companies:', this.companies$);
    this.selectedDeal = null;
    this.modalMode = 'create';
    this.showAddDealModal = true;
  }

  onCloseDealModal(): void {
    this.showAddDealModal = false;
    this.selectedDeal = null;
    this.modalMode = 'create';
  }

  onCloseViewDealModal(): void {
    this.showViewDealModal = false;
  }

  onSaveDeal(dealData: any): void {
    // console.log('Deal data:', dealData, 'Mode:', this.modalMode);

    // API expects single contact_id and company_id, not arrays
    // For now, take the first selected contact and company
    const selectedContactId = dealData.contactIds && dealData.contactIds.length > 0 ? dealData.contactIds[0] : null;
    const selectedCompanyId = dealData.companyIds && dealData.companyIds.length > 0 ? dealData.companyIds[0] : null;

    // Validate required fields
    if (!selectedContactId || !selectedCompanyId) {
      alert('Please select both a contact and a company for the deal.');
      return;
    }

    // Prepare the deal data for the API according to the API specification
    const deal = {
      name: dealData.dealName,
      deal_value: dealData.amount || 0,
      company_id: selectedCompanyId,
      contact_id: selectedContactId,
      icp_score: 0,
      lead_source: 'SalesOMMO',
      user_id: dealData.dealOwner || 1,
      deal_stage: dealData.dealStage || 'early',
      workspace_id: 1,
      close_date: dealData.closeDate,
      type: 'service'
    };

    // console.log('Deal to save:', deal);

    if (this.modalMode === 'create') {
      // Dispatch the createDeal action
      this.store.dispatch(CrmActions.createDeal({ deal }));
    } else if (this.modalMode === 'edit' && this.selectedDeal) {
      // Dispatch the updateDeal action
      this.store.dispatch(CrmActions.updateDeal({
        id: this.selectedDeal.id,
        deal
      }));
    }

    // Close modal immediately (you might want to wait for success)
    this.showAddDealModal = false;
  }

  getDealStageClass(stage: string): string {
    switch (stage?.toLowerCase()) {
      case 'early':
        return 'bg-gray-100 text-gray-800';
      case 'progress':
        return 'bg-blue-100 text-blue-800';
      case 'closing':
        return 'bg-yellow-100 text-yellow-800';
      case 'won / renewal':
      case 'won':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'archived':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  formatCurrency(amount: number): string {
    if (!amount) return '$0';

    // Format for thousands (K), millions (M), billions (B)
    if (amount >= 1000000000) {
      return '$' + (amount / 1000000000).toFixed(1) + 'B';
    } else if (amount >= 1000000) {
      return '$' + (amount / 1000000).toFixed(1) + 'M';
    } else if (amount >= 1000) {
      return '$' + (amount / 1000).toFixed(1) + 'K';
    }

    return '$' + amount.toLocaleString();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Helper methods for null safety
  getCompanyName(deal: any): string {
    return deal?.company?.name || 'No company';
  }

  getContactName(deal: any): string {
    return deal?.contact?.full_name || 'No contact';
  }

  getOwnerName(deal: any): string {
    return deal?.owner?.name || 'Not assigned';
  }
}
